import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { httpService } from "../../../../services/httpService";
import { API_CONFIG, LOCAL_STORAGE_KEYS } from "../../../../shared/constants";
import { ICheckOnGoingIntegrationResponse } from "../interface";
import AddIntegration from "./AddIntegration";
import AddIntegrationSkeleton from "../../../../components/SkeletonLoaders/AddIntegrationSkeleton";

const IntegrationResolver = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [resolvedProps, setResolvedProps] = useState<{
        isEditMode: boolean;
        trackingId: string;
        searchParams: URLSearchParams;
        integratingPlatform: 'google_ads' | 'google_admob' | null;
    } | null>(null);

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const resolveIntegration = async () => {
            setIsLoading(true);
            try {
                // 1. Identify tracking ID
                const savedTrackingId = localStorage.getItem(LOCAL_STORAGE_KEYS.TRACKING_ID);
                const savedMode = localStorage.getItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE);
                let tid = searchParams.get('trackingId') || savedTrackingId;

                let isEditMode = savedMode === 'edit';
                let finalTrackingId = '';

                if (tid && tid !== 'add') {
                    // We have an ID, so it's likely edit mode
                    finalTrackingId = tid;
                } else {
                    // No ID or 'add', create a new one
                    const integration = await httpService.post<ICheckOnGoingIntegrationResponse>(
                        API_CONFIG.path.trackingIntegration
                    );
                    finalTrackingId = integration.tracking_id;
                    isEditMode = false; // Freshly created
                }

                // 2. Identify platform (needed for callback)
                const savedPlatform = localStorage.getItem(LOCAL_STORAGE_KEYS.INTEGRATING_PLATFORM);

                // 3. Set resolved props
                setResolvedProps({
                    isEditMode,
                    trackingId: finalTrackingId,
                    searchParams,
                    integratingPlatform: (savedPlatform as any) || null
                });

                // 4. Delete items from localStorage
                localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATING_PLATFORM);
                localStorage.removeItem(LOCAL_STORAGE_KEYS.INTEGRATION_MODE);
                localStorage.removeItem(LOCAL_STORAGE_KEYS.TRACKING_ID);
                navigate(`/integrations?trackingId=${finalTrackingId}`, { replace: true });
            } catch (error) {
                console.error("Failed to resolve integration:", error);
                // Handle error (maybe redirect or show error)
            } finally {
                setIsLoading(false);
            }
        };

        resolveIntegration();
    }, []);

    if (isLoading || !resolvedProps) return <AddIntegrationSkeleton />;

    return (
        <AddIntegration
            isEditMode={resolvedProps.isEditMode}
            trackingId={resolvedProps.trackingId}
            searchParams={resolvedProps.searchParams}
            integratingPlatform={resolvedProps.integratingPlatform}
        />
    );
};

export default IntegrationResolver;